import { LoginStep } from '../login-step';
export class LoginController {
    $http;
    // Via bindings:
    redirectUrl;
    redirectInTopFrame;
    STEPS = LoginStep;
    error = false;
    /** Current login step. */
    step = LoginStep.USERNAME;
    /** Form data that will be populated during the login process. */
    form = {
        username: new URL(window.location.href).searchParams.get('email') || '',
        password: '',
        token: '',
        verificationStateKey: '',
    };
    /**
     * If enabled the login workflow will attempt to use available SSO providers for
     * the organization of the submitted username to log in after the first step. If
     * not the login will always force the password prompt even while there are SSO
     * providers available.
     *
     * Disabling this serves as a compatibility mode for users that miss-configured
     * their SSO provider and now longer can log in by allowing them to use their
     * snapADDY password instead.
     */
    isSSOEnabled = new URL(window.location.href).searchParams.get('disable_sso') !== '1';
    /**
     * Is `true` if the component is currently loading. During that time the
     * user can not submit any forms.
     */
    isLoading = false;
    // Minification-safe CI injections, must match constructor-arguments
    static $inject = ['$http'];
    constructor($http) {
        this.$http = $http;
    }
    encodeURIComponent(value) {
        return encodeURIComponent(value);
    }
    /** Returns `true` if the username can be submitted. */
    canSubmitUsername() {
        // AngularJS validates an input with "type=email" - only if the value is valid it returns it, otherwise undefined,
        // thus we need to handle that here.
        return this.form.username ? this.form.username.length > 2 : false;
    }
    /**
     * Returns `true` if the password can be submitted.
     */
    canSubmitPassword() {
        return this.form.password.length > 2;
    }
    /**
     * Returns `true` if the two factor token can be submitted.
     */
    canSubmitToken() {
        return this.form.token.length === 6;
    }
    /**
     * Encodes the username for use in URLs.
     */
    getEncodedUsername() {
        return encodeURIComponent(this.form.username);
    }
    /**
     * Submits the username. If the organization of that user has available SSO
     * providers the rest of the login workflow will be delegated there. If not
     * we continue to the next PASSWORD step to allow the user to enter their
     * snapADDY password.
     */
    submitUsername() {
        document.activeElement.blur?.();
        // If SSO login is disabled we simply move onto the password step.
        if (!this.isSSOEnabled) {
            console.info('SSO is disabled.');
            this.step = LoginStep.PASSWORD;
            return;
        }
        this.startLoadingProcess();
        // Get (potential) available SSO providers from the organization of the submitted
        // username. If one is available we redirect the user to the SSO provider,
        // otherwise we move onto the password step and allow the user to log in with
        // their snapADDY password.
        this.$http
            .post('/login/saml/settings', {
            email: this.form.username,
        })
            .then((response) => {
            const samlSignOnUrl = new URL(response.data.url);
            // If this view is within an iframe, the identity provider probably won't allow opening the link in the iframe
            // (security reasons), so we will always open the link at the top level. If this window is already the top
            // window, it's equivalent to the default target _self.
            window.open(`/login/sso?loginUrl=${encodeURIComponent(samlSignOnUrl.href)}&redirect_url=${encodeURIComponent(this.redirectUrl)}`, '_top');
        })
            .catch(() => {
            this.step = LoginStep.PASSWORD;
            this.isLoading = false;
        });
    }
    /**
     * Submits the credentials from the USERNAME and PASSWORD step to attempt to
     * log in.
     *
     * If the user has two-factor enabled they will instead be asked enter their
     * two factor token.
     *
     * On failure (a.E. invalid credentials) the user is reverted to a previous
     * login step. Which step depends on the kind of error. For example if the
     * given password was invalid, we move back to the PASSWORD step. If the
     * credentials were valid but the account was blocked or unconfirmed, we
     * move back to the USERNAME step.
     */
    submitLogin() {
        if (!(this.canSubmitUsername() && this.canSubmitPassword())) {
            this.error = 'AUTH_LOGIN_ERROR_INVALID_CREDENTIALS';
            return;
        }
        document.activeElement.blur?.();
        // Show loading indicator.
        this.startLoadingProcess();
        this.$http
            .post('/login', this.form, {
            params: { redirect_url: this.redirectUrl },
            withCredentials: true,
        })
            .then((response) => {
            this.isLoading = false;
            // If two-factor is enabled we move on to the next step. Otherwise we
            // should already be logged in so we can safely redirect the user.
            if (response.data && 'verificationStateKey' in response.data && response.data.verificationStateKey) {
                this.form.verificationStateKey = response.data.verificationStateKey;
                this.step = LoginStep.TWO_FACTOR;
            }
            else if (response.data && 'redirectUrl' in response.data && response.data.redirectUrl) {
                this.redirect(response.data.redirectUrl);
            }
        })
            .catch((error) => {
            switch (error.data) {
                case 'ACCOUNT_LOCKED':
                    this.error = 'AUTH_LOGIN_ERROR_ACCOUNT_LOCKED';
                    break;
                case 'FORBIDDEN_AUTHENTICATION_TYPE':
                    this.error = 'FORBIDDEN_AUTHENTICATION_TYPE';
                    break;
                case 'INVALID_LOGIN_CREDENTIALS':
                    this.error = 'AUTH_LOGIN_ERROR_INVALID_CREDENTIALS';
                    break;
                case 'FORBIDDEN_FOR_TERMINAL_USER':
                    this.error = 'AUTH_LOGIN_ERROR_TERMINAL_USER';
                    break;
                case 'USER_NOT_CONFIRMED':
                    this.error = 'AUTH_LOGIN_ERROR_UNCONFIRMED';
                    break;
                default:
                    this.error = 'AUTH_LOGIN_ERROR_UNKNOWN';
                    break;
            }
        })
            .finally(() => {
            this.isLoading = false;
        });
    }
    /**
     * Submits the credentials alongside a two factor token to attempt a two
     * factor log in. This is the last step in the log in process, if this
     * fails the user will be asked to enter the two factor token again.
     */
    submitTwoFactorLogin() {
        if (!(this.canSubmitUsername() && this.canSubmitPassword() && this.canSubmitToken())) {
            return;
        }
        // Show loading indicator.
        this.startLoadingProcess();
        this.$http
            .post('/two-factor-code/verify', {
            secondFactorCode: this.form.token,
            verificationStateKey: this.form.verificationStateKey,
        }, { params: { redirect_url: this.redirectUrl } })
            .then((response) => {
            if (response.data && 'redirectUrl' in response.data && response.data.redirectUrl) {
                this.redirect(response.data.redirectUrl);
            }
        })
            .catch((error) => {
            switch (error.data?.error) {
                case 'ACCOUNT_LOCKED':
                    this.error = 'AUTH_LOGIN_ERROR_ACCOUNT_LOCKED';
                    break;
                case 'INVALID_2FA_CODE':
                    this.error = 'AUTH_LOGIN_ERROR_INVALID_TOKEN';
                    break;
                case 'INVALID_STATE':
                    this.form.password = '';
                    this.form.token = '';
                    this.step = LoginStep.USERNAME;
                    this.error = 'AUTH_LOGIN_ERROR_INVALID_STATE';
                    break;
                default:
                    this.error = 'AUTH_LOGIN_ERROR_UNKNOWN';
                    break;
            }
        })
            .finally(() => {
            this.isLoading = false;
        });
    }
    /**
     * Resets all user input and returns the login workflow back to the first
     * step where users have to enter their passwords.
     */
    reset() {
        for (const key of Object.getOwnPropertyNames(this.form)) {
            this.form[key] = '';
        }
        this.step = LoginStep.USERNAME;
    }
    /**
     * Helper function to initialize a load process.
     * @internal
     */
    startLoadingProcess() {
        this.error = false;
        this.isLoading = true;
    }
    redirect(redirectUrl) {
        window.open(redirectUrl, this.redirectInTopFrame ? '_top' : '_self');
    }
}
